import { useCallback, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAssistantConversation, useAssistantMutations } from '@hooks/api/assistant';
import { getAssistantErrorCode } from '@hooks/api/assistant/assistantErrorMessage';

export type AssistantSessionPhase = 'idle' | 'thinking' | 'awaiting' | 'failed';

/**
 * Sprint 37, story-196/199. The URL IS the session state: `/assistant/:conversationId` is that
 * conversation; `/assistant` (optionally `?project=<id>`, read only while there is no id) is a
 * ready, EMPTY new conversation that is not persisted until the first message is sent. Nothing is
 * kept in `localStorage` (the sprint-32 `assistant:lastConversationId:<org>` key is gone).
 *
 * After the first send the returned id is pushed with `history.replace('/assistant/<id>')` once
 * the send mutation has seeded the conversation cache, so the in-flight `THINKING` turn keeps
 * polling with no flash and the same page instance stays mounted.
 */
export function useAssistantSession() {
  const history = useHistory();
  const location = useLocation();
  const { conversationId } = useParams<{ conversationId?: string }>();

  const projectParam = conversationId ? undefined : new URLSearchParams(location.search).get('project') || undefined;

  const { data: conversation, isLoading, error } = useAssistantConversation(conversationId);
  const { send, confirm, dismiss, remove } = useAssistantMutations();
  const [pendingUserText, setPendingUserText] = useState<string | undefined>();

  const notFound = getAssistantErrorCode(error) === 'ASSISTANT_NOT_FOUND';

  const phase: AssistantSessionPhase = (() => {
    if (!conversation) return 'idle';
    if (conversation.status === 'THINKING') return 'thinking';
    if (conversation.status === 'AWAITING_CONFIRMATION') return 'awaiting';
    if (conversation.status === 'FAILED') return 'failed';
    return 'idle';
  })();

  const sendMessage = useCallback(
    async (text: string) => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      setPendingUserText(text);
      try {
        const result = await send.mutateAsync({
          conversationId,
          text,
          // The binding is honoured by the backend only when it creates the conversation.
          projectId: conversationId ? undefined : projectParam,
          timezone,
        });
        if (!conversationId) {
          history.replace(`/assistant/${result.conversationId}`);
        }
      } catch {
        // The mutation's own onError already toasted a translated message.
      } finally {
        setPendingUserText(undefined);
      }
    },
    [send, conversationId, projectParam, history],
  );

  const confirmAction = useCallback(async () => {
    if (!conversationId) return;
    await confirm.mutateAsync(conversationId);
  }, [confirm, conversationId]);

  const dismissAction = useCallback(async () => {
    if (!conversationId) return;
    await dismiss.mutateAsync(conversationId);
  }, [dismiss, conversationId]);

  const startNew = useCallback(() => history.push('/assistant'), [history]);

  const selectConversation = useCallback((id: string) => history.push(`/assistant/${id}`), [history]);

  const deleteConversation = useCallback(
    async (id: string) => {
      await remove.mutateAsync(id);
      // Deleting the open conversation falls back to the empty state, never to another one.
      if (id === conversationId) history.replace('/assistant');
    },
    [remove, conversationId, history],
  );

  const retry = useCallback(async () => {
    if (!conversation) return;
    const lastUserTurn = [...conversation.turns].reverse().find((t) => t.role === 'USER' && t.kind === 'MESSAGE');
    if (!lastUserTurn) return;
    await sendMessage(lastUserTurn.text);
  }, [conversation, sendMessage]);

  return {
    conversationId,
    projectParam,
    conversation,
    phase,
    isLoading,
    notFound,
    pendingUserText,
    sendMessage,
    confirmAction,
    dismissAction,
    startNew,
    selectConversation,
    deleteConversation,
    retry,
    sending: send.isPending,
    confirming: confirm.isPending,
    dismissing: dismiss.isPending,
  };
}

export default useAssistantSession;
